import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/Header';
import { SiteFooter } from '../components/SiteFooter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { CountdownRing } from '../components/CountdownRing';
import { ChunkedUploader } from '../components/ChunkedUploader';
import axios from 'axios';
import { Trophy, Loader2, CheckCircle2, Clock, Target, TrendingUp, Send, Award, Download, MessageCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_META = {
    starter: { emoji: '🥉', color: '#CD7F32' },
    builder: { emoji: '🥈', color: '#B8C4CE' },
    elite: { emoji: '🥇', color: '#FFB800' },
    titan: { emoji: '💎', color: '#22D3EE' },
};

const STATUS_COLORS = {
    winner: '#FFB800', submitted: '#22D3EE', active: '#10B981',
    expired: '#94A3B8', failed: '#EF4444', disqualified: '#EF4444',
};

const CLAIM_MSG = "Hi Nikayofx 🏆 I just WON the 31-Day PTG Challenge and hit my target! I'd like to claim my prize.";

function TierCard({ id, tier, onJoin, joining, t }) {
    const meta = TIER_META[id];
    return (
        <div className="card-surface p-6 flex flex-col" style={{ border: `1px solid ${meta.color}40` }} data-testid={`tier-card-${id}`}>
            <div className="flex items-center justify-between mb-3">
                <span className="text-2xl">{meta.emoji}</span>
                <span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: meta.color }}>{tier.label}</span>
            </div>
            <div className="mb-4 py-3" style={{ borderTop: '1px solid var(--border-subtle)', borderBottom: '1px solid var(--border-subtle)' }}>
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>${tier.capital} <span className="mx-1">→</span></p>
                <p className="text-3xl font-bold font-mono" style={{ color: meta.color }}>${tier.target}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{t('challenge.in31Days')}</p>
            </div>
            <Button className="btn-primary w-full mt-auto" onClick={() => onJoin(id)} disabled={joining} data-testid={`join-tier-${id}`}>
                {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : t('challenge.joinTier')}
            </Button>
        </div>
    );
}

function Leaderboard({ items, t }) {
    if (!items.length) return null;
    return (
        <section className="mt-12" data-testid="challenge-leaderboard">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
                <Trophy className="w-5 h-5" style={{ color: '#FFB800' }} />
                {t('challenge.leaderboard')}
            </h2>
            <div className="card-surface overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr style={{ borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>
                            <th className="text-left py-3 px-4">#</th>
                            <th className="text-left py-3 px-4">{t('challenge.participant')}</th>
                            <th className="text-left py-3 px-4">{t('challenge.tier')}</th>
                            <th className="text-left py-3 px-4">{t('challenge.day')}</th>
                            <th className="text-left py-3 px-4 min-w-[140px]">{t('challenge.progress')}</th>
                            <th className="text-left py-3 px-4">{t('challenge.status')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((c) => {
                            const meta = TIER_META[c.tier] || {};
                            const sc = STATUS_COLORS[c.status] || '#94A3B8';
                            return (
                                <tr key={c.id} style={{ borderBottom: '1px solid var(--border-subtle)' }} data-testid={`leaderboard-row-${c.rank}`}>
                                    <td className="py-3 px-4 font-mono" style={{ color: 'var(--text-muted)' }}>{c.rank}</td>
                                    <td className="py-3 px-4 font-medium" style={{ color: 'var(--text-primary)' }}>
                                        {c.status === 'winner' && '🏆 '}{c.user_name}
                                    </td>
                                    <td className="py-3 px-4"><span style={{ color: meta.color }}>{meta.emoji} {c.tier_label}</span></td>
                                    <td className="py-3 px-4" style={{ color: 'var(--text-secondary)' }}>{c.day}/31</td>
                                    <td className="py-3 px-4">
                                        <div className="flex items-center gap-2">
                                            <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                                                <div className="h-full rounded-full" style={{ width: `${c.progress_pct}%`, backgroundColor: meta.color || '#10B981' }} />
                                            </div>
                                            <span className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>{c.progress_pct}%</span>
                                        </div>
                                    </td>
                                    <td className="py-3 px-4">
                                        <span className="px-2 py-0.5 rounded text-xs font-bold uppercase" style={{ backgroundColor: `${sc}18`, color: sc, border: `1px solid ${sc}40` }}>
                                            {t(`challenge.statuses.${c.status}`)}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

export default function ChallengePage() {
    const { t } = useTranslation();
    const [tiers, setTiers] = useState(null);
    const [me, setMe] = useState(null);
    const [board, setBoard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [joining, setJoining] = useState(false);
    const [balanceInput, setBalanceInput] = useState('');
    const [savingBalance, setSavingBalance] = useState(false);
    const [notebookVideo, setNotebookVideo] = useState('');
    const [accountVideo, setAccountVideo] = useState('');
    const [analyses, setAnalyses] = useState(['', '', '']);
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const fetchAll = useCallback(async () => {
        try {
            const [tRes, mRes, bRes] = await Promise.all([
                axios.get(`${API}/challenge/tiers`),
                axios.get(`${API}/challenge/me`, { withCredentials: true }),
                axios.get(`${API}/challenge/leaderboard`, { withCredentials: true }),
            ]);
            setTiers(tRes.data.tiers);
            setMe(mRes.data);
            setBoard(bRes.data);
        } catch (e) {
            toast.error('Failed to load challenge');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const join = async (tierId) => {
        setJoining(true);
        try {
            await axios.post(`${API}/challenge/join`, { tier: tierId }, { withCredentials: true });
            toast.success(t('challenge.joined'));
            await fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to join');
        } finally {
            setJoining(false);
        }
    };

    const saveBalance = async () => {
        const val = parseFloat(balanceInput);
        if (Number.isNaN(val) || val < 0) return toast.error(t('challenge.invalidBalance'));
        setSavingBalance(true);
        try {
            const res = await axios.post(`${API}/challenge/balance`, { balance: val }, { withCredentials: true });
            setMe((m) => ({ ...m, current: res.data }));
            setBalanceInput('');
            toast.success(t('challenge.balanceSaved'));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        } finally {
            setSavingBalance(false);
        }
    };

    const submit = async () => {
        const filled = analyses.map((a) => a.trim()).filter(Boolean);
        if (!notebookVideo || !accountVideo) return toast.error(t('challenge.uploadBothVideos'));
        if (filled.length < 3) return toast.error(t('challenge.need3Analyses'));
        setSubmitting(true);
        try {
            await axios.post(`${API}/challenge/submit`, {
                notebook_video: notebookVideo,
                account_video: accountVideo,
                analyses: filled,
                note: note || null,
            }, { withCredentials: true });
            toast.success(t('challenge.submitted'));
            await fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to submit');
        } finally {
            setSubmitting(false);
        }
    };

    const c = me?.current;
    const meta = c ? (TIER_META[c.tier] || {}) : {};

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
            <Header />
            <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="challenge-page">
                <div className="mb-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4" style={{ backgroundColor: 'rgba(255,184,0,0.1)', border: '1px solid rgba(255,184,0,0.35)' }}>
                        <Trophy className="w-3.5 h-3.5" style={{ color: '#FFB800' }} />
                        <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#FFB800' }}>{t('challenge.badge')}</span>
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
                        {t('challenge.title')}
                    </h1>
                    <p className="text-base max-w-2xl" style={{ color: 'var(--text-secondary)' }}>{t('challenge.subtitle')}</p>
                </div>

                {loading ? (
                    <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--primary-color)' }} /></div>
                ) : !c || ['expired', 'failed', 'disqualified'].includes(c.status) ? (
                    <>
                        {c && (
                            <div className="mb-6 p-4 rounded-lg flex items-center gap-3" style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }} data-testid="challenge-ended-banner">
                                <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: '#EF4444' }} />
                                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('challenge.previousEnded')} {t('challenge.tryAgain')}</p>
                            </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="tier-selection">
                            {tiers && Object.entries(tiers).map(([id, tier]) => (
                                <TierCard key={id} id={id} tier={tier} onJoin={join} joining={joining} t={t} />
                            ))}
                        </div>
                        <div className="card-surface p-6" data-testid="challenge-rules">
                            <h2 className="text-base font-bold mb-3" style={{ color: 'var(--text-primary)' }}>{t('challenge.rulesTitle')}</h2>
                            <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <li key={i} className="flex gap-2">
                                        <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--primary-color)' }} />
                                        {t(`challenge.rules.${i}`)}
                                    </li>
                                ))}
                            </ul>
                            <p className="text-xs mt-4" style={{ color: '#FFB800' }}>🎁 {t('challenge.prizeNote')}</p>
                        </div>
                    </>
                ) : c.status === 'winner' ? (
                    <div className="card-surface p-8 text-center" data-testid="winner-card">
                        <Award className="w-14 h-14 mx-auto mb-4" style={{ color: '#FFB800' }} />
                        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>🏆 {t('challenge.winnerTitle')}</h2>
                        <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: 'var(--text-secondary)' }}>
                            {t('challenge.winnerText', { tier: c.tier_label, target: c.target })}
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-3">
                            <a href={`${API}/challenge/certificate/${c.id}?download=1`} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm" style={{ backgroundColor: '#FFB800', color: '#0B0F14' }} data-testid="download-certificate-btn">
                                <Download className="w-4 h-4" />
                                {t('challenge.downloadCertificate')}
                            </a>
                            <a href={`https://wa.me/2348081155255?text=${encodeURIComponent(CLAIM_MSG)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm" style={{ backgroundColor: '#25D366', color: '#FFFFFF' }} data-testid="claim-prize-btn">
                                <MessageCircle className="w-4 h-4" />
                                {t('challenge.claimPrize')}
                            </a>
                        </div>
                    </div>
                ) : c.status === 'submitted' ? (
                    <div className="card-surface p-8 text-center" data-testid="submitted-card">
                        <Clock className="w-12 h-12 mx-auto mb-4" style={{ color: '#22D3EE' }} />
                        <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{t('challenge.underReview')}</h2>
                        <p className="text-sm max-w-md mx-auto" style={{ color: 'var(--text-secondary)' }}>{t('challenge.underReviewText')}</p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Active challenge status */}
                        <div className="card-surface p-6" style={{ border: `1px solid ${meta.color}40` }} data-testid="active-challenge-card">
                            <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                                <div className="flex items-center gap-3">
                                    <span className="text-3xl">{meta.emoji}</span>
                                    <div>
                                        <p className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: meta.color }}>{c.tier_label} {t('challenge.tier')}</p>
                                        <p className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>${c.capital} → ${c.target}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="text-right">
                                        <p className="text-2xl font-bold font-mono" style={{ color: 'var(--primary-color)' }} data-testid="challenge-day-counter">{t('challenge.dayOf', { day: c.day })}</p>
                                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('challenge.daysLeft', { count: c.days_left })}</p>
                                    </div>
                                    <CountdownRing daysLeft={c.days_left} totalDays={31} color={meta.color || 'var(--primary-color)'} testId="challenge-countdown-ring" />
                                </div>
                            </div>
                            <div className="mb-2 flex items-center justify-between text-sm">
                                <span style={{ color: 'var(--text-secondary)' }}><TrendingUp className="w-4 h-4 inline mr-1" />{t('challenge.currentBalance')}: <span className="font-mono font-bold" style={{ color: 'var(--text-primary)' }}>${c.current_balance}</span></span>
                                <span className="font-mono font-bold" style={{ color: meta.color }}>{c.progress_pct}%</span>
                            </div>
                            <Progress value={c.progress_pct} className="h-2 mb-5" />
                            <div className="flex gap-2">
                                <Input type="number" min={0} step="0.01" placeholder={t('challenge.balancePlaceholder')} value={balanceInput} onChange={(e) => setBalanceInput(e.target.value)} className="input-field" data-testid="balance-input" />
                                <Button onClick={saveBalance} disabled={savingBalance || balanceInput === ''} data-testid="save-balance-btn">
                                    {savingBalance ? <Loader2 className="w-4 h-4 animate-spin" /> : t('challenge.updateBalance')}
                                </Button>
                            </div>
                        </div>

                        {/* Submission */}
                        <div className="card-surface p-6" data-testid="submission-card">
                            <h2 className="text-base font-bold mb-1 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                                <Target className="w-4 h-4" style={{ color: 'var(--primary-color)' }} />
                                {t('challenge.submitTitle')}
                            </h2>
                            <p className="text-xs mb-5" style={{ color: 'var(--text-muted)' }}>{t('challenge.submitHint')}</p>

                            <div className="space-y-5">
                                <div>
                                    <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>1. {t('challenge.req1')}</p>
                                    <ChunkedUploader kind="video" basePath="challenge/uploads/chunk" maxSizeMB={1024} label={t('challenge.uploadNotebook')} onSuccess={(d) => setNotebookVideo(d.path)} />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>2. {t('challenge.req2')}</p>
                                    <div className="space-y-2">
                                        {analyses.map((a, i) => (
                                            <Textarea key={i} value={a} onChange={(e) => setAnalyses((arr) => arr.map((x, j) => (j === i ? e.target.value : x)))} placeholder={t('challenge.analysisPlaceholder', { num: i + 1 })} className="input-field min-h-[70px]" data-testid={`analysis-input-${i}`} />
                                        ))}
                                        {analyses.length < 10 && (
                                            <button type="button" className="text-xs underline" style={{ color: 'var(--primary-color)' }} onClick={() => setAnalyses((arr) => [...arr, ''])} data-testid="add-analysis-btn">
                                                + {t('challenge.addAnalysis')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>3. {t('challenge.req3')}</p>
                                    <ChunkedUploader kind="video" basePath="challenge/uploads/chunk" maxSizeMB={1024} label={t('challenge.uploadAccount')} onSuccess={(d) => setAccountVideo(d.path)} />
                                </div>
                                <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('challenge.notePlaceholder')} className="input-field min-h-[60px]" data-testid="submission-note" />
                                <Button className="btn-primary w-full" onClick={submit} disabled={submitting} data-testid="submit-challenge-btn">
                                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                                    {t('challenge.submitBtn')}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                <Leaderboard items={board} t={t} />
            </main>
            <SiteFooter compact />
        </div>
    );
}
